import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = { primary: '#2b3380', success: '#4caf50', warning: '#ff9800', error: '#f44336' };

function CustomerDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [customerType, setCustomerType] = useState(searchParams.get('type') || 'b2c');
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchAllData();
  }, [customerType]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [profileRes, statsRes, ordersRes, transactionsRes, ticketsRes, commsRes] = await Promise.all([
        axios.get(`${API}/api/customer-dashboard/profile`, config),
        axios.get(`${API}/api/customer-dashboard/stats`, config),
        axios.get(`${API}/api/customer-dashboard/orders?limit=10`, config),
        axios.get(`${API}/api/customer-dashboard/transactions?limit=10`, config),
        axios.get(`${API}/api/customer-dashboard/tickets?limit=10`, config),
        axios.get(`${API}/api/customer-dashboard/communications?limit=10`, config)
      ]);

      setProfile(profileRes.data.profile);
      setStats(statsRes.data.stats);
      setOrders(ordersRes.data.orders);
      setTransactions(transactionsRes.data.transactions);
      setTickets(ticketsRes.data.tickets);
      setCommunications(commsRes.data.communications);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const customerTypes = [
    { id: 'b2c', label: 'B2C Customer', icon: '🛍️' },
    { id: 'b2r', label: 'B2R Retailer', icon: '🏪' },
    { id: 'b2b', label: 'B2B Buyer', icon: '🏢' },
    { id: 'partner', label: 'Channel Partner', icon: '🤝' }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'orders', label: 'Orders', icon: '📦' },
    { id: 'transactions', label: 'Transactions', icon: '💰' },
    { id: 'tickets', label: 'Support', icon: '🎫' },
    { id: 'communications', label: 'Communications', icon: '💬' }
  ];

  if (loading) {
    return (
      <Layout user={user} onLogout={handleLogout}>
        <div className="p-6 max-w-7xl mx-auto">
          <div className="text-center py-20">
            <div className="text-6xl mb-4">📊</div>
            <div className="text-xl text-gray-600">Loading dashboard...</div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={handleLogout}>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2" style={{ color: COLORS.primary }}>
            👤 Customer Dashboard
          </h1>
          <p className="text-gray-600">View your complete account information and history</p>
        </div>

        {/* Customer Type Selector */}
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="flex gap-4 flex-wrap">
            {customerTypes.map(type => (
              <button
                key={type.id}
                onClick={() => setCustomerType(type.id)}
                className={`px-6 py-3 rounded-lg flex items-center gap-2 transition ${
                  customerType === type.id
                    ? 'text-white'
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
                style={customerType === type.id ? { backgroundColor: COLORS.primary } : {}}
              >
                <span className="text-xl">{type.icon}</span>
                <span className="font-medium">{type.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Profile Summary */}
        {profile && (
          <div className="bg-white rounded-lg shadow mb-6 p-6">
            <h2 className="text-xl font-bold mb-4" style={{ color: COLORS.primary }}>👤 Profile Summary</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <div className="text-sm text-gray-600 mb-1">Name</div>
                <div className="font-medium">{profile.name}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Email</div>
                <div className="font-medium">{profile.email}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Phone</div>
                <div className="font-medium">{profile.phone}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Customer Since</div>
                <div className="font-medium">{new Date(profile.join_date).toLocaleDateString()}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Loyalty Points</div>
                <div className="font-medium text-green-600">🎁 {profile.loyalty_points} points</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Status</div>
                <span className="px-3 py-1 text-sm rounded-full bg-green-100 text-green-800 font-medium capitalize">
                  {profile.status}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Quick Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-600 mb-2">Total Orders</div>
              <div className="text-3xl font-bold" style={{ color: COLORS.primary }}>{stats.total_orders}</div>
              <div className="text-sm text-green-600 mt-1">+{stats.recent_orders} this month</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-600 mb-2">Total Spent</div>
              <div className="text-3xl font-bold text-green-600">₹{stats.total_spent.toLocaleString()}</div>
              <div className="text-sm text-gray-500 mt-1">Lifetime value</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-600 mb-2">Pending Orders</div>
              <div className="text-3xl font-bold text-orange-600">{stats.pending_orders}</div>
              <div className="text-sm text-gray-500 mt-1">In progress</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-600 mb-2">Support Tickets</div>
              <div className="text-3xl font-bold text-blue-600">{stats.open_tickets}</div>
              <div className="text-sm text-gray-500 mt-1">Open tickets</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-300 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 font-medium transition whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-b-4 text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
              style={activeTab === tab.id ? { borderColor: COLORS.primary } : {}}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow">
          {activeTab === 'overview' && (
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4" style={{ color: COLORS.primary }}>Account Overview</h3>
              <div className="space-y-6">
                {/* Recent Orders */}
                <div>
                  <h4 className="font-semibold mb-3">📦 Recent Orders</h4>
                  {orders.length > 0 ? (
                    <div className="space-y-2">
                      {orders.slice(0, 3).map(order => (
                        <div key={order.id} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                          <div>
                            <div className="font-medium">{order.order_number}</div>
                            <div className="text-sm text-gray-600">{new Date(order.order_date).toLocaleDateString()}</div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold">₹{order.total_amount}</div>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                              order.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {order.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No recent orders</p>
                  )}
                </div>

                {/* Recent Transactions */}
                <div>
                  <h4 className="font-semibold mb-3">💰 Recent Transactions</h4>
                  {transactions.length > 0 ? (
                    <div className="space-y-2">
                      {transactions.slice(0, 3).map(txn => (
                        <div key={txn.id} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                          <div>
                            <div className="font-medium">{txn.reference_number}</div>
                            <div className="text-sm text-gray-600 capitalize">{txn.type} • {txn.payment_method}</div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold">₹{txn.amount}</div>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              txn.status === 'completed' ? 'bg-green-100 text-green-800' :
                              txn.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {txn.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No transactions yet</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'orders' && (
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4" style={{ color: COLORS.primary }}>📦 Order History</h3>
              {orders.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order #</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {orders.map(order => (
                        <tr key={order.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium">{order.order_number}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{new Date(order.order_date).toLocaleDateString()}</td>
                          <td className="px-4 py-3 text-sm">{order.items_count}</td>
                          <td className="px-4 py-3 font-semibold">₹{order.total_amount}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs rounded-full capitalize ${
                              order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                              order.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                              order.status === 'confirmed' ? 'bg-purple-100 text-purple-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {order.status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs rounded-full capitalize ${
                              order.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                              order.payment_status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {order.payment_status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No orders yet</p>
              )}
            </div>
          )}

          {activeTab === 'transactions' && (
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4" style={{ color: COLORS.primary }}>💰 Transaction History</h3>
              {transactions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference #</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {transactions.map(txn => (
                        <tr key={txn.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium">{txn.reference_number}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{new Date(txn.transaction_date).toLocaleDateString()}</td>
                          <td className="px-4 py-3 text-sm capitalize">{txn.type}</td>
                          <td className="px-4 py-3 text-sm capitalize">{txn.payment_method.replace('_', ' ')}</td>
                          <td className="px-4 py-3 font-semibold">₹{txn.amount}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs rounded-full capitalize ${
                              txn.status === 'completed' ? 'bg-green-100 text-green-800' :
                              txn.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {txn.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No transactions yet</p>
              )}
            </div>
          )}

          {activeTab === 'tickets' && (
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4" style={{ color: COLORS.primary }}>🎫 Support Tickets</h3>
              {tickets.length > 0 ? (
                <div className="space-y-4">
                  {tickets.map(ticket => (
                    <div key={ticket.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="font-semibold">{ticket.ticket_number}</div>
                          <div className="text-gray-800">{ticket.subject}</div>
                        </div>
                        <div className="flex gap-2">
                          <span className={`px-2 py-1 text-xs rounded-full capitalize ${
                            ticket.status === 'resolved' ? 'bg-green-100 text-green-800' :
                            ticket.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                            ticket.status === 'open' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {ticket.status.replace('_', ' ')}
                          </span>
                          <span className={`px-2 py-1 text-xs rounded-full capitalize ${
                            ticket.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                            ticket.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                            ticket.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {ticket.priority}
                          </span>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">{ticket.description}</div>
                      <div className="text-xs text-gray-500 mt-2">Created: {new Date(ticket.created_at).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No support tickets</p>
              )}
            </div>
          )}

          {activeTab === 'communications' && (
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4" style={{ color: COLORS.primary }}>💬 Communication History</h3>
              {communications.length > 0 ? (
                <div className="space-y-3">
                  {communications.map((comm, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                      <div className="text-3xl">
                        {comm.type === 'whatsapp' ? '💬' : comm.type === 'email' ? '📧' : '📞'}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium capitalize">{comm.type}: {comm.subject || 'Communication'}</div>
                        <div className="text-sm text-gray-600">{new Date(comm.date).toLocaleString()}</div>
                      </div>
                      {comm.status && (
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 capitalize">
                          {comm.status}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No communications yet</p>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default CustomerDashboard;